import Input from "@material-ui/core/Input";
import { createStyles, Theme, WithStyles, withStyles } from "@material-ui/core/styles";
import classNames from "classnames";
import moment from "moment";
import React from "react";
import DateTime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import { DateTimePickerOptions } from "./models";

const DateTimePickerWidth = 250;

const styles = (theme: Theme) =>
  createStyles({
    container: {
      flexGrow: 1,
      position: "relative",
      "& .rdtPicker": {
        marginTop: 0,
        width: DateTimePickerWidth,
        border: 0,
        boxShadow: theme.shadows[2],
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        fontFamily: theme.typography.fontFamily,
      },
      "& .rdtPicker th": {
        borderBottomColor: theme.palette.divider,
      },
      "& .rdtPicker td.rdtMonth:hover, td.rdtYear:hover": {
        backgroundColor: theme.palette.action.hover,
      },
      "& .rdtPicker thead tr:first-child th:hover": {
        backgroundColor: theme.palette.action.hover,
      },
      "& .rdtPicker td.rdtDay:hover, .rdtPicker .rdtTimeToggle:hover": {
        backgroundColor: theme.palette.action.hover,
      },
      "& .rdtPicker td.rdtHour:hover, .rdtPicker td.rdtMinute:hover, .rdtPicker td.rdtSecond:hover": {
        backgroundColor: theme.palette.action.hover,
      },
      "& .rdtPicker td.rdtActive, .rdtPicker td.rdtActive:hover": {
        backgroundColor: theme.palette.action.selected,
        color: theme.palette.text.primary,
        textShadow: "none",
      },
      "& .rdtPicker td.rdtToday:before": {
        borderBottomColor: theme.palette.secondary.main,
      },
      "& .rdtPicker .rdtCounters": {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      "& .rdtPicker .rdtCounter": {
        height: "auto",
        width: "auto",
      },
      "& .rdtPicker .rdtCounterSeparator": {
        lineHeight: "unset",
        marginTop: -3,
      },
      "& .rdtPicker .rdtCounter .rdtBtn": {
        height: "auto",
        lineHeight: "unset",
        padding: theme.spacing(1),
      },
      "& .rdtPicker .rdtCounter .rdtCount": {
        height: "auto",
        fontSize: "unset",
        padding: theme.spacing(1),
      },
      "& .rdtPicker .rdtCounter .rdtBtn:hover": {
        backgroundColor: theme.palette.action.hover,
      },
    },
    containerRightAlignDatePicker: {
      "& .rdtPicker": {
        right: 0,
      },
    },
    textField: {
      marginTop: theme.spacing(2),
      width: "100%",
    },
  });

interface DateTimePickerProps extends WithStyles<typeof styles> {
  id: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  value: string | null | undefined;
  valueFormat?: {
    date?: string;
    time?: string | false;
  };
  options?: DateTimePickerOptions;
  className?: string;
  processValue?: (value: string | null | undefined) => string | null | undefined;
  onChange: (value: string | null | undefined) => void;
}

interface DateTimePickerState {
  value: string | null | undefined;
  alignment: "left" | "right";
}

export const DateTimePickerComponent = class extends React.Component<DateTimePickerProps, DateTimePickerState> {
  public valueFormat = {
    date: "YYYY-MM-DD",
    time: "HH:mm",
  };
  public inputRef: React.RefObject<HTMLInputElement>;
  public inputBlurTimeout: number | undefined;

  constructor(props: DateTimePickerProps) {
    super(props);

    const { value: isoDateTimeString, valueFormat: valueFormatOptions } = this.props;

    // If the default Date format is to be overridden...
    if (valueFormatOptions && valueFormatOptions.date) {
      this.valueFormat.date = valueFormatOptions.date;
    }
    // If the default Time format is to be overridden...
    if (valueFormatOptions) {
      // If the Time portion of the Date/Time Picker is to be used...
      if (valueFormatOptions.time) {
        this.valueFormat.time = valueFormatOptions.time;
      }
      // Otherwise, if the Time portion is not to be used (i.e. just a Date Picker)...
      else if (valueFormatOptions.time === false) {
        this.valueFormat.time = "";
      }
    }

    const value = isoDateTimeString && this.toDateTimeString(moment(isoDateTimeString));

    this.state = {
      value,
      alignment: "left",
    };

    this.inputRef = React.createRef<HTMLInputElement>();
  }

  public render() {
    const { id, disabled, placeholder, label, className, classes } = this.props;
    const { value, alignment } = this.state;

    const containerClassName = classNames(
      classes.container,
      alignment === "right" ? classes.containerRightAlignDatePicker : undefined,
      className,
    );

    const dateTimePickerProps = {
      className: containerClassName,
      renderInput: this.renderInput,
      inputProps: {
        id,
        className: classes.textField,
        disabled,
        label,
        placeholder,
        ref: this.inputRef,
        autoComplete: "off",
        onBlur: this.handleInputBlur,
      },
      value,
      dateFormat: this.valueFormat.date,
      timeFormat: this.valueFormat.time || false,
      closeOnSelect: true,
      onChange: this.handleChange,
      onBlur: this.handleBlur,
      isValidDate: this.handleIsValidDate,
    } as DateTime.DatetimepickerProps;

    return <DateTime {...dateTimePickerProps} />;
  }

  public componentDidUpdate(prevProps: DateTimePickerProps) {
    const { value: prevValue } = prevProps;
    const { value: nextValue } = this.props;
    const { alignment: currAlignment } = this.state;

    let alignment = currAlignment;
    const dateTimePickerBoundingClientRect = this.inputRef.current && this.inputRef.current.getBoundingClientRect();
    const dateTimePickerCollidingAtRight =
      dateTimePickerBoundingClientRect &&
      dateTimePickerBoundingClientRect.left + DateTimePickerWidth > window.innerWidth;
    // If the DateTimePicker is colliding with the right edge of the viewport, and it is not yet right-aligned...
    if (dateTimePickerCollidingAtRight && alignment !== "right") {
      alignment = "right";
    }
    // Otherwise, ensure the alignment is left if it isn't already...
    else if (!dateTimePickerCollidingAtRight && alignment !== "left") {
      alignment = "left";
    }

    // If the value was updated outside the input (i.e. custom formData manipulation at form-level)...
    if (prevValue !== nextValue) {
      const value = nextValue && this.toDateTimeString(moment(nextValue));

      this.setState({ ...this.state, value });
    }
    if (currAlignment !== alignment) {
      this.setState({ ...this.state, alignment });
    }
  }

  public renderInput = (inputProps: any) => {
    // Discard placeholder, as we're using a shrinkable label instead
    const { className, value, ref, placeholder, disabled, ...remainingProps } = inputProps;
    const { onClick, onFocus, onKeyDown, onChange, ...remainingPropsWithoutHandlers } = remainingProps;

    let props = remainingProps;

    // If the input is disabled, do not wire up the event handlers to prevent the calendar from opening...
    if (disabled) {
      props = remainingPropsWithoutHandlers;
    }

    return <Input className={className} value={value || ""} inputRef={ref} disabled={disabled} {...props} />;
  };

  public handleIsValidDate = (current: moment.Moment | string) => {
    const { options } = this.props;

    if (options) {
      const { validStartDate, validEndDate } = options as DateTimePickerOptions;

      const validStart = moment(validStartDate).startOf("day");
      const validEnd = moment(validEndDate).endOf("day");

      let currentMoment: moment.Moment;

      if (moment.isMoment(current)) {
        currentMoment = current;
      } else {
        currentMoment = moment(current);
      }

      let isValidDate = true;

      if (validStartDate && validEndDate) {
        isValidDate = currentMoment.isBetween(validStart, validEnd, undefined, "[]");
      } else if (validStartDate) {
        isValidDate = currentMoment.isSameOrAfter(validStart);
      } else if (validEndDate) {
        isValidDate = currentMoment.isSameOrBefore(validEnd);
      }

      return isValidDate;
    }

    return true;
  };

  public handleChange = (value: moment.Moment | string) => {
    let dateTimeString;
    // If a valid date/time has been specified, then a moment has been passed in...
    if (moment.isMoment(value)) {
      dateTimeString = this.toDateTimeString(value);
    } else {
      // Otherwise, if the user is still specifying a valid date/time, and a string has been passed in...
      dateTimeString = value;
    }

    this.setState({ ...this.state, value: dateTimeString });
  };

  public handleBlur = (value: moment.Moment | string) => {
    const { processValue, onChange } = this.props;

    // If the input blurred, but the picker has actually blurred, handle blur here instead of via input handler...
    if (this.inputBlurTimeout) {
      clearTimeout(this.inputBlurTimeout);

      this.inputBlurTimeout = undefined;
    }

    const { dateTimeString, isoDateTimeString } = this.processDateTimeValue(value);

    this.setState({ ...this.state, value: dateTimeString }, () => {
      const processedValue = processValue
        ? (processValue(isoDateTimeString) as string | null | undefined)
        : isoDateTimeString && isoDateTimeString.trim();

      onChange(processedValue);
    });
  };

  public handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { processValue, onChange } = this.props;
    const { value } = this.props;

    const inputElement = event.target as HTMLInputElement;
    const inputValue = inputElement.value;

    this.inputBlurTimeout = window.setTimeout(() => {
      const { dateTimeString, isoDateTimeString } = this.processDateTimeValue(inputValue);
      // If the value has changed, schedule a setState/onChange from this inputBlur event, in case the following
      // DateTimePicker's blur event does not fire (which happens in the case of copy/paste, autocomplete, etc.)...
      if (value !== isoDateTimeString) {
        this.setState({ ...this.state, value: dateTimeString }, () => {
          const processedValue = processValue
            ? (processValue(isoDateTimeString) as string | null | undefined)
            : isoDateTimeString && isoDateTimeString.trim();

          onChange(processedValue);
        });
      }
    }, 150);
  };

  public processDateTimeValue = (value: moment.Moment | string) => {
    if (typeof value === "string") {
      const noColonTimeMatch = /^(\d{4}-\d{1,2}-\d{1,2}) (\d{3,4}|\d{1,2}:\d{2})$/.exec(value);
      // If the incoming string-based value is in the format yyyy-MM-dd [H]Hmm, parse it is a date/time value
      // where the time value is lacking a colon...
      if (noColonTimeMatch) {
        const colonPosition = value.length - 2;
        const colonTimeValue = `${value.substring(0, colonPosition)}:${value.substring(colonPosition, value.length)}`;

        value = moment(colonTimeValue, this.getDateTimeFormatString());
      } else {
        value = moment(value, this.getDateTimeFormatString());
      }
    }

    let dateTimeString: string | undefined;
    let isoDateTimeString: string | undefined;
    // If a valid date/time has been specified, then a moment has been passed in...
    if (moment.isMoment(value) && value.isValid()) {
      dateTimeString = this.toDateTimeString(value);
      // If a Time portion is involved, use moment's toISOString utility method which formats the ISO8601 string to
      // always include the Time portion...
      if (this.valueFormat.time) {
        isoDateTimeString = value.toISOString();
      }
      // Otherwise, if a Time portion is not involved, then format the isoDateTimeString without moment's toISOString
      // utility method (which always includes the Time potion), and instead simply format as YYYY-MM-DD, which is the
      // expected format for a Date-only ISO8601 string (https://en.wikipedia.org/wiki/ISO_8601)...
      else {
        isoDateTimeString = value.format("YYYY-MM-DD");
      }
    }

    return { dateTimeString, isoDateTimeString };
  };

  public getDateTimeFormatString = () => {
    return this.valueFormat.date + (this.valueFormat.time ? ` ${this.valueFormat.time}` : "");
  };

  public toDateTimeString = (value: moment.Moment) => {
    return value ? value.format(this.getDateTimeFormatString()) : undefined;
  };
};

export const DateTimePicker = withStyles(styles)(DateTimePickerComponent);
